import { useState } from "react";
import { Search, X, Lightbulb, Star, Users, BookOpen, Target } from "lucide-react";
import Navbar from "./Navbar";

const cultureTips = [
  {
    id: 1,
    title: "Bowing & Respect",
    description: "Bowing is a standard form of greeting and showing respect in Korea. A slight bow is common when saying hello, while deeper bows show greater respect, especially to elders.",
    category: "Etiquette",
    difficulty: "Beginner",
    examples: [
      "안녕하세요 (Hello) - accompanied by a slight bow",
      "감사합니다 (Thank you) - bow while saying this",
      "죄송합니다 (Sorry) - bow to show sincerity"
    ],
    icon: "🙇‍♂️"
  },
  {
    id: 2,
    title: "Texting & Slang",
    description: "Korean texting culture includes unique slang and abbreviations. Understanding these helps you communicate naturally with Korean friends.",
    category: "Language",
    difficulty: "Intermediate",
    examples: [
      "ㅋㅋㅋ (laughter) - equivalent to 'haha'",
      "ㅎㅇ (hi) - abbreviation of 안녕",
      "ㄱㅅ (thanks) - abbreviation of 감사",
      "ㄴㄴ (no) - abbreviation of 아니"
    ],
    icon: "📱"
  },
  {
    id: 3,
    title: "Food & Dining",
    description: "Korean meals are often shared with side dishes (banchan). It's polite to wait for elders to begin eating and to use both hands when receiving or giving items.",
    category: "Food Culture",
    difficulty: "Beginner",
    examples: [
      "맛있게 드세요 (Enjoy your meal) - said before eating",
      "잘 먹었습니다 (I ate well) - said after finishing",
      "건배! (Cheers!) - when drinking together"
    ],
    icon: "🍜"
  },
  {
    id: 4,
    title: "K-Drama & Music",
    description: "Korean entertainment has become a global phenomenon. Understanding cultural references in K-dramas and K-pop helps with language learning.",
    category: "Entertainment",
    difficulty: "Beginner",
    examples: [
      "오빠 (older brother) - used by younger women",
      "언니 (older sister) - used by younger women",
      "형 (older brother) - used by younger men"
    ],
    icon: "🎭"
  },
  {
    id: 5,
    title: "Honorifics & Formality",
    description: "Korean has complex honorific systems. Using the right level of formality shows respect and cultural awareness.",
    category: "Language",
    difficulty: "Intermediate",
    examples: [
      "~세요 (formal ending) - used with strangers/elders",
      "~어요/아요 (polite ending) - general polite form",
      "~야/아 (casual ending) - used with close friends"
    ],
    icon: "👑"
  },
  {
    id: 6,
    title: "Family Structure",
    description: "Korean families are traditionally hierarchical. Understanding family terms and relationships is crucial for social interactions.",
    category: "Social Structure",
    difficulty: "Beginner",
    examples: [
      "할머니 (grandmother) - father's mother",
      "할아버지 (grandfather) - father's father",
      "외할머니 (grandmother) - mother's mother"
    ],
    icon: "👨‍👩‍👧‍👦"
  },
  {
    id: 7,
    title: "Business Etiquette",
    description: "Korean business culture emphasizes hierarchy, respect, and relationship-building. Understanding these norms is essential for professional success.",
    category: "Business",
    difficulty: "Advanced",
    examples: [
      "명함 (business card) - exchange with both hands",
      "회식 (company dinner) - important for relationships",
      "과장님 (manager) - respectful title usage"
    ],
    icon: "💼"
  },
  {
    id: 8,
    title: "Traditional Holidays",
    description: "Korean holidays like Chuseok and Seollal have deep cultural significance. Learning about these helps understand Korean values and traditions.",
    category: "Traditions",
    difficulty: "Beginner",
    examples: [
      "설날 (New Year) - family gatherings and ancestral rites",
      "추석 (Harvest Festival) - thanksgiving celebration",
      "어린이날 (Children's Day) - celebrating children"
    ],
    icon: "🎊"
  },
  {
    id: 9,
    title: "Age & Hierarchy",
    description: "Age plays a crucial role in Korean social interactions. Even small age differences affect how people address each other and interact.",
    category: "Social Structure",
    difficulty: "Intermediate",
    examples: [
      "동갑 (same age) - can speak casually",
      "형/누나 (older) - younger person's address",
      "동생 (younger) - older person's address"
    ],
    icon: "🎂"
  },
  {
    id: 10,
    title: "Gift Giving",
    description: "Gift-giving in Korea follows specific etiquette. Understanding these customs helps build relationships and show respect.",
    category: "Etiquette",
    difficulty: "Intermediate",
    examples: [
      "선물 (gift) - often given in pairs",
      "받으세요 (please receive) - polite offering",
      "고마워요 (thank you) - gracious acceptance"
    ],
    icon: "🎁"
  },
  {
    id: 11,
    title: "Public Transportation",
    description: "Korean public transport is efficient and widely used. Understanding etiquette helps you navigate smoothly and respectfully.",
    category: "Lifestyle",
    difficulty: "Beginner",
    examples: [
      "지하철 (subway) - quiet conversations preferred",
      "버스 (bus) - offer seats to elderly",
      "택시 (taxi) - polite greetings expected"
    ],
    icon: "🚇"
  },
  {
    id: 12,
    title: "Health & Wellness",
    description: "Korean health culture includes traditional medicine (한의학) alongside modern practices. Understanding these concepts enriches cultural knowledge.",
    category: "Health",
    difficulty: "Intermediate",
    examples: [
      "한의학 (traditional medicine) - holistic approach",
      "찜질방 (sauna) - communal relaxation",
      "건강 (health) - highly valued concept"
    ],
    icon: "🏥"
  },
  {
    id: 13,
    title: "Education Culture",
    description: "Education is highly valued in Korean society. Understanding the education system and student culture provides insight into Korean values.",
    category: "Lifestyle",
    difficulty: "Intermediate",
    examples: [
      "선생님 (teacher) - highly respected title",
      "공부 (studying) - serious commitment",
      "대학 (university) - important life goal"
    ],
    icon: "🎓"
  },
  {
    id: 14,
    title: "Technology & Innovation",
    description: "Korea is a global leader in technology. Understanding Korean tech culture helps appreciate the country's modern achievements.",
    category: "Lifestyle",
    difficulty: "Beginner",
    examples: [
      "스마트폰 (smartphone) - essential daily tool",
      "인터넷 (internet) - high-speed connectivity",
      "게임 (gaming) - popular entertainment"
    ],
    icon: "📱"
  },
  {
    id: 15,
    title: "Fashion & Beauty",
    description: "Korean fashion and beauty trends influence global culture. Understanding these aspects helps appreciate Korean aesthetic values.",
    category: "Lifestyle",
    difficulty: "Beginner",
    examples: [
      "패션 (fashion) - trendy and practical",
      "뷰티 (beauty) - skincare emphasis",
      "스타일 (style) - individual expression"
    ],
    icon: "👗"
  },
  {
    id: 16,
    title: "Environmental Consciousness",
    description: "Korea places increasing emphasis on environmental protection. Understanding these values shows awareness of modern Korean priorities.",
    category: "Lifestyle",
    difficulty: "Intermediate",
    examples: [
      "환경 (environment) - growing concern",
      "재활용 (recycling) - common practice",
      "친환경 (eco-friendly) - valued concept"
    ],
    icon: "🌱"
  }
];

const categories = ["All", "Etiquette", "Language", "Food Culture", "Entertainment", "Social Structure", "Lifestyle", "Traditions", "Business", "Health"];

function CultureTips() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedTip, setSelectedTip] = useState(null);
  const [filteredTips, setFilteredTips] = useState(cultureTips);

  const handleSearch = (term) => {
    setSearchTerm(term);
    filterTips(term, selectedCategory);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    filterTips(searchTerm, category);
  };

  const filterTips = (search, category) => {
    let filtered = cultureTips;

    if (search) {
      filtered = filtered.filter(tip =>
        tip.title.toLowerCase().includes(search.toLowerCase()) ||
        tip.description.toLowerCase().includes(search.toLowerCase()) ||
        tip.category.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (category !== "All") {
      filtered = filtered.filter(tip => tip.category === category);
    }

    setFilteredTips(filtered);
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case "Beginner": return "bg-green-100 text-green-800";
      case "Intermediate": return "bg-orange-100 text-orange-800";
      case "Advanced": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "Etiquette": return "bg-pink-100 text-pink-800";
      case "Language": return "bg-red-100 text-red-800";
      case "Food Culture": return "bg-orange-100 text-orange-800";
      case "Entertainment": return "bg-purple-100 text-purple-800";
      case "Social Structure": return "bg-blue-100 text-blue-800";
      case "Lifestyle": return "bg-green-100 text-green-800";
      case "Traditions": return "bg-yellow-100 text-yellow-800";
      case "Business": return "bg-indigo-100 text-indigo-800";
      case "Health": return "bg-teal-100 text-teal-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-100 to-red-100">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-amber-100 to-orange-100 shadow-2xl border-b-4 border-red-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-16">
            <div className="text-center">
              <div className="flex flex-col sm:flex-row items-center justify-center mb-6 sm:mb-8">
                <div className="relative">
                  <div className="absolute -inset-6 bg-gradient-to-r from-red-500 to-orange-500 rounded-full blur-lg opacity-30 animate-pulse"></div>
                  <div className="h-20 w-20 sm:h-28 sm:w-28 flex items-center justify-center relative z-10">
                    <span className="text-5xl sm:text-7xl font-black text-red-700">화</span>
                  </div>
                </div>
                <div className="sm:ml-6 mt-4 sm:mt-0">
                  <h1 className="text-3xl sm:text-5xl font-black text-gray-900 mb-2">Korean Culture Guide</h1>
                  <p className="text-lg sm:text-2xl text-red-700 font-black">한국 문화 가이드</p>
                  <p className="text-sm sm:text-lg text-gray-600 font-semibold">(Korean Culture Guide)</p>
                </div>
              </div>
              
              <p className="text-xl text-gray-800 max-w-4xl mx-auto leading-relaxed font-semibold mb-8">
                Discover the rich traditions, customs, and social norms that make Korean culture unique. Understanding culture is key to mastering the language!
              </p>

              {/* Search Bar */}
              <div className="max-w-2xl mx-auto mb-8">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-6 h-6" />
                  <input
                    type="text"
                    placeholder="Search cultural topics..."
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 text-lg border-4 border-red-200 rounded-2xl focus:outline-none focus:border-red-400 transition-colors duration-300 shadow-lg"
                  />
                </div>
              </div>

              {/* Category Filter */}
              <div className="flex flex-wrap justify-center gap-4">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => handleCategoryChange(category)}
                    className={`px-6 py-3 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg ${
                      selectedCategory === category
                        ? "bg-gradient-to-r from-red-600 to-orange-600 text-white shadow-xl"
                        : "bg-white text-gray-700 hover:bg-red-50 border-2 border-red-200 hover:border-red-400"
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Stats Section */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-16">
            <div className="bg-gradient-to-br from-pink-100 to-rose-100 rounded-3xl shadow-xl p-8 border-4 border-pink-200 text-center">
              <div className="text-6xl font-black text-pink-700 mb-2">{cultureTips.length}</div>
              <div className="text-xl font-bold text-gray-800 mb-2">Cultural Tips</div>
              <div className="text-sm text-gray-600 font-semibold">Learn Korean customs</div>
            </div>
            <div className="bg-gradient-to-br from-green-100 to-emerald-100 rounded-3xl shadow-xl p-8 border-4 border-green-200 text-center">
              <div className="text-6xl font-black text-green-700 mb-2">{categories.length - 1}</div>
              <div className="text-xl font-bold text-gray-800 mb-2">Categories</div>
              <div className="text-sm text-gray-600 font-semibold">Diverse topics</div>
            </div>
            <div className="bg-gradient-to-br from-orange-100 to-amber-100 rounded-3xl shadow-xl p-8 border-4 border-orange-200 text-center">
              <div className="text-6xl font-black text-orange-700 mb-2">{filteredTips.length}</div>
              <div className="text-xl font-bold text-gray-800 mb-2">Filtered Results</div>
              <div className="text-sm text-gray-600 font-semibold">Current search</div>
            </div>
            <div className="bg-gradient-to-br from-purple-100 to-indigo-100 rounded-3xl shadow-xl p-8 border-4 border-purple-200 text-center">
              <div className="text-6xl font-black text-purple-700 mb-2">3</div>
              <div className="text-xl font-bold text-gray-800 mb-2">Difficulty Levels</div>
              <div className="text-sm text-gray-600 font-semibold">Beginner to Advanced</div>
            </div>
          </div>

          {/* Tips Grid */}
          {filteredTips.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredTips.map((tip) => (
                <div
                  key={tip.id}
                  className="bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border-4 border-red-200 hover:border-red-400 overflow-hidden cursor-pointer"
                  onClick={() => setSelectedTip(tip)}
                >
                  <div className="p-8">
                    <div className="flex items-center justify-between mb-6">
                      <div className="text-4xl">{tip.icon}</div>
                      <span className={`px-3 py-1 rounded-full text-sm font-bold ${getDifficultyColor(tip.difficulty)}`}>
                        {tip.difficulty}
                      </span>
                    </div>
                    
                    <h3 className="text-2xl font-black text-gray-900 mb-4 group-hover:text-red-700 transition-colors duration-300">
                      {tip.title}
                    </h3>
                    
                    <p className="text-gray-700 leading-relaxed mb-6 font-medium">
                      {tip.description}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <span className={`px-3 py-1 rounded-full text-sm font-bold ${getCategoryColor(tip.category)}`}>
                        {tip.category}
                      </span>
                      <div className="flex items-center text-gray-600">
                        <Lightbulb className="w-4 h-4 mr-1" />
                        <span className="text-sm font-semibold">{tip.examples.length} examples</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="bg-white rounded-3xl shadow-xl p-12 border-4 border-red-200 max-w-2xl mx-auto">
                <div className="text-8xl mb-8">🔍</div>
                <h3 className="text-3xl font-black text-gray-900 mb-4">No cultural tips found</h3>
                <p className="text-xl text-gray-700 mb-8 font-semibold">
                  Try adjusting your search terms or category filter to find more cultural insights.
                </p>
                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => {
                      setSearchTerm("");
                      setSelectedCategory("All");
                      setFilteredTips(cultureTips);
                    }}
                    className="bg-gradient-to-r from-red-600 to-orange-600 text-white px-8 py-4 rounded-2xl font-bold hover:from-red-700 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    Show All Tips
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal */}
        {selectedTip && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border-4 border-red-200">
              <div className="p-8">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center">
                    <div className="text-5xl mr-6">{selectedTip.icon}</div>
                    <div>
                      <h2 className="text-4xl font-black text-gray-900 mb-2">{selectedTip.title}</h2>
                      <div className="flex items-center gap-4">
                        <span className={`px-3 py-1 rounded-full text-sm font-bold ${getDifficultyColor(selectedTip.difficulty)}`}>
                          {selectedTip.difficulty}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-sm font-bold ${getCategoryColor(selectedTip.category)}`}>
                          {selectedTip.category}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedTip(null)}
                    className="bg-gradient-to-r from-red-600 to-orange-600 text-white p-3 rounded-full hover:from-red-700 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                <div className="mb-8">
                  <p className="text-xl text-gray-700 leading-relaxed font-medium">
                    {selectedTip.description}
                  </p>
                </div>
                
                <div>
                  <h3 className="text-2xl font-black text-gray-900 mb-6">Examples:</h3>
                  <div className="space-y-4">
                    {selectedTip.examples.map((example, index) => (
                      <div key={index} className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-6 border-2 border-red-200">
                        <p className="text-lg text-gray-800 font-semibold">{example}</p>
            </div>
          ))}
        </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default CultureTips;